import { Container } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ProductFilters from 'components/filters';
import MasonryProductGrid from 'components/products/masonryProductGrid';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { getCMSSettings } from 'tools/cmsBlockSettings';
import {
	getIdsFromListData,
	getNewParams,
	paramsHash,
	getAllProducts,
	prepareFilters,
	isPropertyKey,
	updateRouterData, getNewFilterParams,
} from 'functions/plpOps';

// Masonry PLP elements
import ProductInfiniteScroll from 'components/navigation/infinite-scroll';
import { ScrollContext } from 'components/providers/scroll';
import { TrackingContext } from 'components/providers/tracking';
import { useTranslation } from 'tools/i18n';
import theme from "../../../theme";
import useSessionStore from '@services/stores/SessionStore';

const useStyles = makeStyles(
	() => ({
		wrapper: {
			paddingBottom: 40
		},
		filterWrapper: {
			margin: '0px 0 10px',
			[theme.breakpoints.down('sm')]: {
				paddingLeft: 20,
				paddingRight: 20
			}
		},
		preTitle: {
			textAlign: 'center',
			color: '#666',
			fontSize: 14,
			fontWeight: 400,
			letterSpacing: '0.125em',
			textTransform: 'uppercase'
		},
		name: {
			textAlign: 'center',
			color: '#459ED7',
			fontFamily: 'Red Bright, serif',
			fontSize: 80,
			fontWeight: 400,
			[theme.breakpoints.down('sm')]: {
				fontSize: '55px !important',
				marginBottom: 10
			},
		},
		desc: {
			textAlign: 'center',
			fontSize: 18,
			color: '#333333',
			lineHeight: 'normal',
			marginBottom: 10,
			fontWeight: 300,
			[theme.breakpoints.down('sm')]: {
				fontSize: 16,
			},
		},
		readMore: {
			fontSize: 18,
			color: '#333333',
			display: 'inline-block',
			lineHeight: 'normal',
			marginBottom: 20,
			textDecoration: 'none',
			[theme.breakpoints.down('sm')]: {
				fontSize: 16,
			},
		}
	}),
	{ name: 'MasonryPLP' }
);

function MasonryPLP({ block, breadcrumbs, category, categoryMenu }) {
	const { t } = useTranslation();
	const classes = useStyles();
	const { actions } = useContext(TrackingContext);
	const { setCache, getCache, isPopState } = useContext(ScrollContext);
	const CMSSettings = getCMSSettings(block, '0px');
	const prodListData = block.slots[0];
	const productListing = prodListData.data ? prodListData.data.listing : { elements: [], limit: 48, page: 1 };
	const currentFilters = prodListData.data ? prodListData.data.listing.currentFilters : {};
	const defaultSorting = prodListData.data ? prodListData.data.listing.sorting : 'topseller';
	const availableFilters = prodListData.data ? prodListData.data.listing.aggregations : {};

	const session = useSessionStore((state) => state.session);
	const router = useRouter();
	const { query, asPath } = router;
	const [initial, setInitial] = useState(true);
	const [products, setProducts] = useState(productListing.elements);
	const [pagination, setPagination] = useState({
		initialPage: productListing.page,
		loadedPages: [productListing.page]
	});
	const [categorySlug, setCategorySlug] = useState(asPath.split('?')[0]);
	const { page, order, slug, ...filters } = query;

	useEffect(() => {
		let newCategoryPath = asPath.split('?')[0];

		if (initial || newCategoryPath !== categorySlug) {
			setInitial(true);
			setCategorySlug(asPath.split('?')[0]);
			setPagination({ initialPage: parseInt(query.page) || 1, loadedPages: [parseInt(query.page) || 1] });
		}
	}, [asPath]);

	useEffect(() => {
		if (isPopState && initial) {
			let cachedCategory = getCache(router.asPath);

			if (cachedCategory) {
				setInitial(false);
				setPagination(cachedCategory.pagination);
				setProducts(cachedCategory.products);
			}
		}
	}, [asPath]);

	useEffect(() => {
		if (!initial) {
			// Update pagination with the now loaded page
			let loadedPages = pagination.loadedPages;
			if (loadedPages.indexOf(productListing.page) < 0) {
				if (productListing.page > pagination.initialPage) {
					loadedPages.push(productListing.page);
				} else if (productListing.page) {
					loadedPages.unshift(productListing.page);
				}

				setPagination({ ...pagination, loadedPages });
			}

			setProducts(getAllProducts(products, prodListData, pagination));

			/**
			 * Save products to localstorage for custom history state management
			 */
			let stateKey = router.asPath;
			setCache(stateKey, { pagination, products: getAllProducts(products, prodListData, pagination) });
		} else {
			if(isPopState) {
				setPagination({ initialPage: parseInt(query.page) || 1, loadedPages: [parseInt(query.page) || 1] });
			}

			setProducts(prodListData.data.listing.elements);
		}
	}, [prodListData]);

	useEffect(() => {
		let currentPage = parseInt(page) || 1;

		setPagination(pagination => {
			return { ...pagination, currentPage };
		});
	}, [page]);

	function onFilterChange(attribute, data) {
		setInitial(true);
		setPagination({ initialPage: 1, loadedPages: [1] });
		updateUrlParams({ ...getNewFilterParams(page, query, isPropertyKey(availableFilters, attribute) ? 'properties' : attribute, data, availableFilters) });
	}

	function onPageChange(value) {
		setInitial(false);
		updateUrlParams({ ...getNewParams(value, query) });
	}

	function updateUrlParams({ page, order, slug, ...filters }) {
		const { routerData, replace } = updateRouterData({
			page,
			order,
			initialPage: pagination.initialPage,
			pathname: router.asPath.split('?')[0],
			filters,
			availableFilters
		});

		if (routerData.pathname.includes('#')) {
			routerData.pathname = routerData.pathname.split('#')[0];
		}

		if (replace) {
			router.replace(routerData, undefined, { scroll: false }).catch(e => console.log(e));
		} else {
			router.push(routerData, undefined, { scroll: false }).catch(e => console.log(e));
		}
	}

	function renderCategoryHero(readMore = true) {
		if (!category?.translated?.description) {
			return <div style={{height: '30px'}}></div>;
		}

		const preTitle = categorySlug.includes('slim-aarons') ? 'Slim Aarons' : 'Gallery';

		return <Container style={{ maxWidth: 835, textAlign: 'center', margin: '60px auto 40px auto' }}>
			<div className={classes.preTitle}>{preTitle}</div>
			<div className={classes.name}>{category.translated.name}</div>
			<div className={classes.desc}>{category.translated.description}</div>
			{readMore ? <div className={classes.readMore} href='#story' onClick={handleScroll}>
				<span>Read more...</span>
			</div> : ''}
		</Container>
	}

	const handleScroll = (e) => {
		e.preventDefault();
		const href = e.currentTarget.href;
		const targetId = href.replace(/.*#/, "");
		const elem = document.getElementById(targetId);
		elem?.scrollIntoView({
			behavior: "smooth",
		});
	};

	return (
		<>
			<Container
				className={classes.wrapper}
				style={{
					...CMSSettings.styles
				}}
				disableGutters
			>
				{!!category ? renderCategoryHero(CMSSettings.class !== 'noscroll') : ''}

				<div className={classes.filterWrapper}>
					<ProductFilters
						id={'filters'}
						currentSorting={order || defaultSorting}
						currentFilters={currentFilters}
						filters={prepareFilters(availableFilters, session.filters, prodListData, true)}
						availableFilters={availableFilters}
						categories={categoryMenu}
						onFilterChange={onFilterChange}
						category={category}
						isMasonry={true}
					/>
				</div>

				{productListing.total > productListing.limit ? (
					<ProductInfiniteScroll
						currentPage={pagination.currentPage}
						loadedPages={pagination.loadedPages}
						loadedProducts={products.length}
						totalProducts={productListing.total}
						productsPerPage={productListing.limit}
						changePage={onPageChange}
						position={'top'}
					/>
				) : (
					''
				)}
				<MasonryProductGrid
					products={products}
					productListing={productListing}
					breadcrumbs={breadcrumbs}
					list={{name: category.translated.name, id: '', location: router.asPath}}
					salesChannelId={session.salesChannel ? session.salesChannel.id : ''}
					lazyHydrate={initial}
					paramsHash={paramsHash(query)}
				/>
				<ProductInfiniteScroll
					currentPage={pagination.currentPage}
					loadedPages={pagination.loadedPages}
					loadedProducts={products.length}
					totalProducts={productListing.total}
					productsPerPage={productListing.limit}
					changePage={onPageChange}
					position={'bottom'}
				/>
			</Container>
			<div id={'story'}/>
		</>
	);
}

export default MasonryPLP;
